import * as React from 'react';

import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * Tailwind-merge does not recognize the `tw:` prefix, so `DialogContent`'s own
 * `grid`, `gap-4` and `p-6` survive a plain override and have to be beaten with
 * `!important`.
 */
const SURFACE =
  'dark tw:!flex tw:max-h-[85vh] tw:!flex-col tw:!gap-0 tw:overflow-hidden tw:bg-background tw:!p-0 tw:text-foreground';

const DEFAULT_WIDTH = 'tw:!max-w-lg tw:sm:!max-w-lg';

export type FormModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Action buttons, pinned below the scroll region. */
  footer?: React.ReactNode;
  /**
   * Must set `tw:sm:!max-w-*` too, since DialogContent hard-codes
   * `sm:max-w-md`.
   */
  widthClassName?: string;
  /** Optional fixed height (e.g. `tw:h-[85vh]`) so the sheet does not shrink. */
  heightClassName?: string;
  /**
   * Wraps the whole modal in a `<form>`, so a submit button in the footer still
   * submits even though it sits outside the scrolling body.
   */
  formProps?: React.ComponentProps<'form'>;
  children: React.ReactNode;
  /** Stays visible above the scrolling body, below the title. */
  banner?: React.ReactNode;
};

/**
 * Centered modal for a form that may outgrow the viewport: the header and
 * footer stay put while the body scrolls.
 */
export function FormModal({
  open,
  onOpenChange,
  title,
  description,
  footer,
  widthClassName = DEFAULT_WIDTH,
  heightClassName,
  formProps,
  children,
  banner,
}: FormModalProps) {
  const regions = (
    <>
      <DialogHeader className="tw:shrink-0 tw:border-b tw:border-border tw:px-6 tw:py-4">
        <DialogTitle className="tw:pr-8 tw:text-base">{title}</DialogTitle>
        {description ? (
          <DialogDescription className="tw:pr-8">
            {description}
          </DialogDescription>
        ) : null}
      </DialogHeader>

      {banner ? <div className="tw:shrink-0">{banner}</div> : null}

      <div className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:px-6 tw:py-4">
        {children}
      </div>

      {footer ? (
        <DialogFooter className="tw:shrink-0 tw:flex-row tw:justify-end tw:gap-2 tw:border-t tw:border-border tw:px-6 tw:py-4">
          {footer}
        </DialogFooter>
      ) : null}
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(SURFACE, widthClassName, heightClassName)}>
        {formProps ? (
          <form
            {...formProps}
            className={cn(
              'tw:flex tw:min-h-0 tw:flex-1 tw:flex-col',
              formProps.className
            )}
          >
            {regions}
          </form>
        ) : (
          regions
        )}
      </DialogContent>
    </Dialog>
  );
}
